import React, { useState, useRef, useMemo } from 'react';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { Download, Upload, X, Loader2 } from 'lucide-react';
import { extractBorrowersImport } from '../../services/borrowersImportService';
import { createClient, markInvitationSent } from '../../services/clientsService';
import { queueInvitationEmail } from '../../services/invitationEmailService';
import { getLenderSettings } from '../../services/lenderSettingsService';
import {
  createLenderStagedProperty,
  findFirstInvalidStagedTrancheDateRow,
  STAGED_TRANCHE_DATES_INVALID,
} from '../../services/lenderStagedPropertiesService';
import {
  formatStagingPrincipal,
  formatStagingPercent,
  parseMoneyLoose,
  parsePercentLoose,
  roundMoneyToCents,
} from '../../utils/stagingAmountFormat';

function isValidEmail(s) {
  if (!s || typeof s !== 'string') return false;
  const e = s.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function stagingAddressLine(row) {
  return String(row.streetAddress || row.projectTitle || '').trim();
}

function stagingNotes(row, t) {
  const bits = [];
  if (row.notes) bits.push(String(row.notes).trim());
  if (row.streetAddress && row.projectTitle) {
    bits.push(`${t('clients.importNoteProjectPrefix')}${row.projectTitle}`);
  }
  return bits.join('\n\n').slice(0, 500);
}

function tranchesForService(loanTranches) {
  if (!Array.isArray(loanTranches) || loanTranches.length === 0) return [];
  return loanTranches.map((tr) => ({
    amount: tr.amount != null ? String(tr.amount) : '',
    startDate: tr.startDate || '',
    interestRate: tr.interestRate != null && tr.interestRate !== '' ? String(tr.interestRate) : '',
    maturityDate: tr.maturityDate || '',
    note: tr.note || '',
  }));
}

function sumTranchePrincipal(tranches) {
  if (!Array.isArray(tranches)) return 0;
  const s = tranches.reduce((acc, tr) => {
    if (tr == null) return acc;
    if (typeof tr.amount === 'number' && Number.isFinite(tr.amount)) {
      return acc + roundMoneyToCents(tr.amount);
    }
    const p = parseMoneyLoose(tr.amount);
    if (Number.isFinite(p)) return acc + p;
    const n = Number(tr.amount);
    return acc + (Number.isFinite(n) ? roundMoneyToCents(n) : 0);
  }, 0);
  return roundMoneyToCents(s);
}

function getTranchePrincipalNumber(tr) {
  if (typeof tr?.amount === 'number' && Number.isFinite(tr.amount)) {
    return roundMoneyToCents(tr.amount);
  }
  const p = parseMoneyLoose(tr?.amount);
  if (Number.isFinite(p)) return p;
  if (tr?.amount != null && tr.amount !== '') {
    const n = Number(tr.amount);
    if (Number.isFinite(n)) return roundMoneyToCents(n);
  }
  return NaN;
}

function getTrancheRateNumber(tr) {
  const rateRaw = tr?.interestRate;
  if (rateRaw == null || rateRaw === '') return NaN;
  const rp = parsePercentLoose(rateRaw);
  if (Number.isFinite(rp)) return rp;
  const n = Number(rateRaw);
  return Number.isFinite(n) ? n : NaN;
}

/** First selected row index per email — one createClient per email in commit order. */
function buildFirstSelectedIndexByEmail(rows) {
  const m = new Map();
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row.selected || !isValidEmail(row.email)) continue;
    const em = String(row.email).trim().toLowerCase();
    if (!m.has(em)) m.set(em, i);
  }
  return m;
}

function importRowMetaList(rows, existingEmailSet) {
  const firstSel = buildFirstSelectedIndexByEmail(rows);
  const seenEmailAbove = new Set();
  return rows.map((row, i) => {
    const em = String(row.email || '').trim().toLowerCase();
    const ok = isValidEmail(row.email);
    const emailDuplicateEarlierInFile = ok && seenEmailAbove.has(em);
    if (ok) seenEmailAbove.add(em);

    const addr = stagingAddressLine(row);
    const lt = tranchesForService(row.loanTranches);
    const badDates =
      row.includeBuilding && addr && findFirstInvalidStagedTrancheDateRow(lt) >= 0;
    const badEmail = row.selected && !ok;

    const alreadyInApp = ok && existingEmailSet.has(em);
    const isFirstSelectedForEmail = ok && row.selected && firstSel.get(em) === i;
    const hasBuilding = row.includeBuilding && !!addr;

    let canCommit = false;
    if (row.selected && ok && !badDates) {
      if (!alreadyInApp) {
        if (isFirstSelectedForEmail) canCommit = true;
        else if (hasBuilding) canCommit = true;
      } else if (hasBuilding) {
        canCommit = true;
      }
    }

    return {
      canCommit,
      badEmail,
      badDates,
      alreadyInApp,
      emailDuplicateEarlierInFile,
    };
  });
}

export default function BorrowersImportModal({
  open,
  onClose,
  lenderUid,
  displayName,
  existingClients,
  t,
  language,
  onImported,
}) {
  const [step, setStep] = useState('pick');
  const [fileLabel, setFileLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const inputRef = useRef(null);

  const reset = () => {
    setStep('pick');
    setFileLabel('');
    setLoading(false);
    setCommitting(false);
    setError('');
    setRows([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const existingEmailSet = useMemo(
    () =>
      new Set(
        (existingClients || []).map((c) => String(c.email || '').trim().toLowerCase()).filter(Boolean)
      ),
    [existingClients]
  );

  const rowMetas = useMemo(() => importRowMetaList(rows, existingEmailSet), [rows, existingEmailSet]);
  const hasCommitable = useMemo(() => rowMetas.some((m) => m.canCommit), [rowMetas]);

  const importPreviewTotals = useMemo(() => {
    let all = 0;
    let selected = 0;
    rows.forEach((row) => {
      const s = sumTranchePrincipal(row.loanTranches);
      all += s;
      if (row.selected) selected += s;
    });
    return { all: roundMoneyToCents(all), selected: roundMoneyToCents(selected) };
  }, [rows]);

  const pickFile = async (file) => {
    if (!file) return;
    setError('');
    setFileLabel(file.name);
    setLoading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => {
          const res = String(r.result || '');
          const idx = res.indexOf('base64,');
          resolve(idx >= 0 ? res.slice(idx + 7) : res);
        };
        r.onerror = () => reject(new Error('read failed'));
        r.readAsDataURL(file);
      });
      const data = await extractBorrowersImport(b64, file.name, language);
      const list = data.borrowers || [];
      setRows(
        list.map((b) => ({
          ...b,
          selected: true,
          includeBuilding: !!(
            stagingAddressLine(b) ||
            (Array.isArray(b.loanTranches) && b.loanTranches.length > 0)
          ),
        }))
      );
      setStep('preview');
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (i, patch) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };

  const invitePayload = (clientId, emailTo, clientName, invitationLink) => ({
    to: emailTo,
    clientName: clientName || undefined,
    invitationLink,
    clientId,
    invitationKind: 'private_lender_borrower',
    invitationScenario: 'migration',
  });

  const buildInvitationLink = (token) => {
    if (!token || typeof window === 'undefined') return '';
    return `${window.location.origin}${window.location.pathname}#/signup?invitation=${token}`;
  };

  const handleCommit = async () => {
    setError('');
    setCommitting(true);
    let borrowersCreated = 0;
    let buildingsAdded = 0;
    let failed = 0;
    let skipAutoInvite = false;
    try {
      const ls = await getLenderSettings(lenderUid);
      skipAutoInvite = ls?.fundInConfiguration === true;
    } catch (_) {
      /* ignore */
    }

    const firstSel = buildFirstSelectedIndexByEmail(rows);
    const clientIdByEmail = new Map();
    (existingClients || []).forEach((c) => {
      const em = String(c.email || '').trim().toLowerCase();
      if (em) clientIdByEmail.set(em, c.id);
    });

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const meta = rowMetas[i];
      if (!meta.canCommit) {
        if (row.selected && isValidEmail(row.email) && !meta.badDates) {
          /* selected but nothing to do e.g. existing borrower, no building */
        } else if (row.selected && (!isValidEmail(row.email) || meta.badDates)) {
          failed += 1;
        }
        continue;
      }

      const email = String(row.email || '').trim().toLowerCase();
      const addr = stagingAddressLine(row);
      const lt = tranchesForService(row.loanTranches);
      const hasBuilding = row.includeBuilding && !!addr;
      const isFirstSelected = firstSel.get(email) === i;

      let clientId = clientIdByEmail.get(email);

      if (!clientId && (isFirstSelected || hasBuilding)) {
        try {
          const { clientId: newId, invitationToken } = await createClient(lenderUid, {
            name: row.name || '',
            email,
            brokerDisplayName: displayName || '',
          });
          clientId = newId;
          clientIdByEmail.set(email, clientId);
          borrowersCreated += 1;
          if (!skipAutoInvite) {
            try {
              await queueInvitationEmail(
                lenderUid,
                invitePayload(clientId, email, row.name, buildInvitationLink(invitationToken))
              );
              await markInvitationSent(clientId);
            } catch (e) {
              console.error('Import invite queue:', e);
            }
          }
        } catch (e) {
          console.error('createClient import:', e);
          failed += 1;
          continue;
        }
      }

      if (!clientId) {
        failed += 1;
        continue;
      }

      if (hasBuilding) {
        try {
          await createLenderStagedProperty({
            lenderUid,
            clientId,
            address: addr,
            city: row.city || '',
            province: row.province || '',
            postalCode: row.postalCode || '',
            notes: stagingNotes(row, t),
            loanTranches: lt,
          });
          buildingsAdded += 1;
        } catch (e) {
          console.error('Staged property:', e);
          if (e?.message === STAGED_TRANCHE_DATES_INVALID) {
            failed += 1;
          }
        }
      }
    }

    setCommitting(false);
    if (typeof onImported === 'function') onImported();

    const anySuccess = borrowersCreated > 0 || buildingsAdded > 0;
    if (anySuccess && failed > 0) {
      alert(
        t('clients.importPartialSummary')
          .replace('{br}', String(borrowersCreated))
          .replace('{bd}', String(buildingsAdded))
          .replace('{bad}', String(failed))
      );
    } else if (borrowersCreated > 0 && buildingsAdded > 0) {
      alert(
        t('clients.importSuccessBorrowersAndBuildings')
          .replace('{b}', String(borrowersCreated))
          .replace('{d}', String(buildingsAdded))
      );
    } else if (borrowersCreated > 0) {
      alert(t('clients.importSuccess').replace('{n}', String(borrowersCreated)));
    } else if (buildingsAdded > 0) {
      alert(t('clients.importSuccessBuildings').replace('{n}', String(buildingsAdded)));
    } else if (failed > 0) {
      alert(t('clients.importNothingCreated'));
    } else {
      alert(t('clients.importNoSelection'));
    }
    handleClose();
  };

  useRegisterTopEscapeClose(handleClose, open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t('clients.importBorrowersTitle')}</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100"
            aria-label={t('close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {step === 'pick' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{t('clients.importBorrowersHint')}</p>
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-3">
                <p className="text-xs font-semibold text-gray-800 mb-2">{t('clients.importBorrowersExpectedTitle')}</p>
                <ul className="list-disc list-outside ms-4 space-y-1.5 text-sm text-gray-700">
                  <li>{t('clients.importBorrowersExpectedBorrower')}</li>
                  <li>{t('clients.importBorrowersExpectedEmail')}</li>
                  <li>{t('clients.importBorrowersExpectedAddress')}</li>
                  <li>{t('clients.importBorrowersExpectedTranches')}</li>
                  <li>{t('clients.importBorrowersExpectedAmount')}</li>
                  <li>{t('clients.importBorrowersExpectedRate')}</li>
                </ul>
              </div>
              <p className="text-xs text-gray-600">{t('clients.importBorrowersTemplateRecommended')}</p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              <div className="flex flex-wrap gap-2">
                <a
                  href="/templates/borrowers-import-template.xlsx"
                  download="rentalyzer-borrowers-import-example.xlsx"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50"
                >
                  <Download className="w-5 h-5" />
                  {t('clients.importBorrowersDownloadTemplate')}
                </a>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => inputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  {loading ? t('clients.importAnalyzing') : t('clients.importChooseFile')}
                </button>
              </div>
              {fileLabel && <p className="text-xs text-gray-500">{fileLabel}</p>}
            </div>
          )}
          {step === 'preview' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{t('clients.importPreviewHelp')}</p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full min-w-[880px] text-sm table-fixed">
                  <colgroup>
                    <col className="w-10" />
                    <col className="min-w-[7rem]" />
                    <col className="w-[9.5rem]" />
                    <col className="min-w-[6rem]" />
                    <col className="min-w-[7rem]" />
                    <col className="w-[22%]" />
                    <col className="w-24" />
                  </colgroup>
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="p-2"> </th>
                      <th className="p-2">{t('clients.importName')}</th>
                      <th className="p-2">{t('clients.importEmail')}</th>
                      <th className="p-2">{t('clients.importProjectTitle')}</th>
                      <th className="p-2">{t('clients.importStreet')}</th>
                      <th className="p-2">{t('clients.importTranchesColumn')}</th>
                      <th className="p-2 text-center">{t('clients.importIncludeBuilding')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const meta = rowMetas[i] || {
                        canCommit: false,
                        badEmail: false,
                        badDates: false,
                        alreadyInApp: false,
                        emailDuplicateEarlierInFile: false,
                      };
                      const badEmail = meta.badEmail;
                      const badDates = meta.badDates;
                      const warnRow =
                        row.selected &&
                        (meta.alreadyInApp || meta.emailDuplicateEarlierInFile) &&
                        !badEmail &&
                        !badDates;
                      return (
                        <tr
                          key={i}
                          className={
                            badEmail || badDates
                              ? 'bg-red-50'
                              : warnRow
                                ? 'bg-amber-50/80'
                                : ''
                          }
                        >
                          <td className="p-2 align-top">
                            <input
                              type="checkbox"
                              checked={row.selected}
                              onChange={(e) => updateRow(i, { selected: e.target.checked })}
                            />
                          </td>
                          <td className="p-2 align-top">
                            <input
                              className="w-full min-w-[8rem] border rounded px-1 py-0.5 text-xs"
                              value={row.name || ''}
                              onChange={(e) => updateRow(i, { name: e.target.value })}
                            />
                          </td>
                          <td className="p-2 align-top max-w-[9.5rem]">
                            <input
                              className={`w-full max-w-full border rounded px-1 py-0.5 text-xs break-all ${badEmail ? 'border-red-500' : ''}`}
                              value={row.email || ''}
                              onChange={(e) => updateRow(i, { email: e.target.value })}
                              title={row.email || ''}
                            />
                            {meta.alreadyInApp && (
                              <p className="text-xs text-amber-800">{t('clients.importExistingBorrowerBuildingsOnly')}</p>
                            )}
                            {meta.emailDuplicateEarlierInFile && !meta.alreadyInApp && (
                              <p className="text-xs text-amber-800">{t('clients.importSameEmailGroupedHint')}</p>
                            )}
                            {badEmail && row.selected && (
                              <p className="text-xs text-red-600">{t('clients.importNoEmail')}</p>
                            )}
                          </td>
                          <td className="p-2 align-top">
                            <input
                              className="w-full min-w-[8rem] border rounded px-1 py-0.5 text-xs"
                              value={row.projectTitle || ''}
                              onChange={(e) => updateRow(i, { projectTitle: e.target.value })}
                            />
                          </td>
                          <td className="p-2 align-top">
                            <input
                              className="w-full min-w-[8rem] border rounded px-1 py-0.5 text-xs"
                              value={row.streetAddress || ''}
                              onChange={(e) => updateRow(i, { streetAddress: e.target.value })}
                            />
                          </td>
                          <td className="p-2 align-top text-xs text-gray-800 align-middle">
                            {Array.isArray(row.loanTranches) && row.loanTranches.length > 0 ? (
                              <div className="space-y-0 max-h-40 overflow-y-auto pr-0.5 text-left">
                                {row.loanTranches.map((tr, ti) => {
                                  const p = getTranchePrincipalNumber(tr);
                                  const r = getTrancheRateNumber(tr);
                                  const sd = tr?.startDate ? String(tr.startDate).trim().slice(0, 10) : '';
                                  const md = tr?.maturityDate ? String(tr.maturityDate).trim().slice(0, 10) : '';
                                  return (
                                    <div
                                      key={ti}
                                      className="border-b border-slate-100 pb-2 mb-2 last:border-0 last:pb-0 last:mb-0"
                                    >
                                      <div className="leading-tight text-gray-900">
                                        {Number.isFinite(p) && p > 0 ? (
                                          <>
                                            {formatStagingPrincipal(p, language)}
                                            {Number.isFinite(r) && (
                                              <>
                                                {' '}
                                                {t('clients.importTrancheAt')}{' '}
                                                {formatStagingPercent(r, language)}
                                              </>
                                            )}
                                          </>
                                        ) : (
                                          <span className="text-gray-400">—</span>
                                        )}
                                      </div>
                                      {(sd || md) && (
                                        <div className="leading-tight text-gray-600 mt-0.5">
                                          {t('clients.importTrancheDateLine')
                                            .replace('{start}', sd || '—')
                                            .replace('{end}', md || '—')}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                <div className="pt-1 mt-0.5 border-t border-slate-200 font-medium text-gray-900">
                                  {t('clients.importRowSubtotal')}{' '}
                                  {formatStagingPrincipal(sumTranchePrincipal(row.loanTranches), language)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="p-2 align-top text-center">
                            <input
                              type="checkbox"
                              checked={row.includeBuilding}
                              onChange={(e) => updateRow(i, { includeBuilding: e.target.checked })}
                              title={t('clients.importIncludeBuilding')}
                            />
                            {badDates && (
                              <p className="text-xs text-red-600 mt-1 text-left">
                                {t('clients.stagedTrancheMaturityAfterStart')}
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t-2 border-slate-200 bg-slate-50/90">
                    <tr>
                      <td colSpan={5} className="p-2 text-right text-xs font-semibold text-gray-800 align-middle">
                        {t('clients.importPreviewTotalPrincipal')}
                      </td>
                      <td className="p-2 text-xs font-semibold text-gray-900 tabular-nums align-middle">
                        {formatStagingPrincipal(importPreviewTotals.all, language)}
                      </td>
                      <td className="p-2 align-middle" />
                    </tr>
                    <tr>
                      <td colSpan={5} className="p-2 pb-3 text-right text-xs text-gray-600 align-middle">
                        {t('clients.importPreviewTotalSelected')}
                      </td>
                      <td className="p-2 pb-3 text-xs text-gray-800 tabular-nums align-middle">
                        {formatStagingPrincipal(importPreviewTotals.selected, language)}
                      </td>
                      <td className="p-2 align-middle" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200"
          >
            {t('cancel')}
          </button>
          {step === 'preview' && (
            <button
              type="button"
              disabled={committing || !hasCommitable}
              onClick={handleCommit}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {committing ? '…' : t('clients.importConfirm')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
